/**
 * URL handoff: ?token=<jwt> → strip from address bar, exchange once via refresh API, then refresh on a 20-minute timer only (not on API 401).
 * Access token is stored in a cookie and sent on API calls via mergeAuthHeaders (Authorization: Bearer).
 * Configure: REACT_APP_AUTH_REFRESH_URL (optional; defaults to same API root + /api/cfs-security-broker/v1/refreshToken).
 */

import { apiUrl } from '../api/config';

const STORAGE_KEY = 'cfs_access_token';
const REFRESH_MS = (() => {
  const mins = Number(process.env.REACT_APP_TOKEN_REFRESH_MINUTES);
  if (Number.isFinite(mins) && mins > 0) return mins * 60 * 1000;
  return 20 * 60 * 1000; // default: 20 minutes
})();
/** Cookie lifetime; extended on each successful refresh. */
const COOKIE_MAX_AGE_SEC = 7 * 24 * 60 * 60; // 7 days

/**
 * Refresh URL: explicit env, else same-origin /api/… (CRA dev proxy → Node → CFS upstream),
 * else REACT_APP_API_URL root.
 */
function resolveRefreshUrl() {
  const fromEnv = (process.env.REACT_APP_AUTH_REFRESH_URL || '').trim();
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/cfs-security-broker/v1/refreshToken`;
  }
  return apiUrl('/api/cfs-security-broker/v1/refreshToken');
}

let refreshIntervalId = null;
let didMigrateLegacySessionStorage = false;

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const idx = part.indexOf('=');
    if (idx === -1) continue;
    const k = part.slice(0, idx).trim();
    if (k !== name) continue;
    const v = part.slice(idx + 1).trim();
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  }
  return null;
}

function setCookie(name, value, maxAgeSeconds) {
  if (typeof document === 'undefined') return;
  const attrs = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${Math.floor(maxAgeSeconds)}`,
    'SameSite=Lax',
  ];
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    attrs.push('Secure');
  }
  document.cookie = attrs.join('; ');
}

function deleteCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = `${encodeURIComponent(name)}=; path=/; max-age=0`;
}

/** One-time: copy legacy sessionStorage token into cookie; only clear session if cookie holds the full value. */
function migrateLegacySessionStorageOnce() {
  if (didMigrateLegacySessionStorage) return;
  didMigrateLegacySessionStorage = true;
  try {
    const legacy = sessionStorage.getItem(STORAGE_KEY);
    if (!legacy) return;
    if (!getCookie(STORAGE_KEY)) {
      setCookie(STORAGE_KEY, legacy, COOKIE_MAX_AGE_SEC);
    }
    const fromCookie = getCookie(STORAGE_KEY);
    // Large JWTs may not fit in a cookie — keep sessionStorage if cookie truncated or missing
    if (fromCookie === legacy) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Persist access token after refresh.
 * JWTs are often >4KB; browser cookies can fail silently at that size, so we always
 * mirror to sessionStorage too. Reads use sessionStorage first if cookie value mismatches.
 */
function persistAccessToken(token) {
  deleteCookie(STORAGE_KEY);
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch (e) {
    console.warn('[auth] Could not mirror token to sessionStorage', e);
  }
  setCookie(STORAGE_KEY, token, COOKIE_MAX_AGE_SEC);
  const fromCookie = getCookie(STORAGE_KEY);
  if (fromCookie && fromCookie !== token) {
    console.warn(
      '[auth] Cookie may be truncated (size limit ~4KB); using sessionStorage copy for API headers.',
    );
  }
}

/** Clear stored token (e.g. sign-out). */
export function clearAccessToken() {
  deleteCookie(STORAGE_KEY);
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Current access token for API calls (Bearer).
 * Prefer sessionStorage when present (reliable for large JWTs); else cookie.
 */
export function getAccessToken() {
  migrateLegacySessionStorageOnce();
  try {
    const fromSession = sessionStorage.getItem(STORAGE_KEY);
    const fromCookie = getCookie(STORAGE_KEY);
    if (fromSession && fromCookie && fromSession !== fromCookie) {
      return fromSession;
    }
    return fromSession || fromCookie;
  } catch {
    return null;
  }
}

/** `Authorization: Bearer <token>` when a token is stored. */
export function getAuthHeader() {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Merge your headers with Bearer auth so every fetch can include the token. */
export function mergeAuthHeaders(headers = {}) {
  return { ...headers, ...getAuthHeader() };
}

function clearRefreshInterval() {
  if (refreshIntervalId != null) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

/** Rough JWT shape: three base64url segments (handles typical access tokens). */
function looksLikeJwt(value) {
  if (typeof value !== 'string' || value.length < 30) return false;
  const parts = value.split('.');
  return parts.length >= 3 && parts.every((p) => p.length > 0);
}

/** Last resort: find any string in JSON that looks like a JWT (unknown API shapes). */
function deepFindJwtLike(obj, depth = 0) {
  if (depth > 10 || obj == null) return null;
  if (typeof obj === 'string' && looksLikeJwt(obj)) return obj;
  if (typeof obj !== 'object') return null;
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = deepFindJwtLike(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  const preferredKeys = [
    'accessToken',
    'access_token',
    'idToken',
    'id_token',
    'token',
    'jwt',
    'bearerToken',
    'authToken',
  ];
  for (const key of preferredKeys) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && typeof obj[key] === 'string' && looksLikeJwt(obj[key])) {
      return obj[key];
    }
  }
  for (const k of Object.keys(obj)) {
    const found = deepFindJwtLike(obj[k], depth + 1);
    if (found) return found;
  }
  return null;
}

function pickTokenFromResponse(data) {
  if (data == null) return null;
  if (typeof data === 'string' && looksLikeJwt(data)) return data;

  if (!data || typeof data !== 'object') return null;

  const nested = data.data != null && typeof data.data === 'object' ? data.data : null;
  const result = data.result != null && typeof data.result === 'object' ? data.result : null;
  const payload = data.payload != null && typeof data.payload === 'object' ? data.payload : null;

  const candidates = [
    data.accessToken,
    data.access_token,
    data.token,
    data.jwt,
    data.idToken,
    data.id_token,
    data.bearerToken,
    data.authToken,
    nested?.accessToken,
    nested?.access_token,
    nested?.token,
    nested?.jwt,
    nested?.idToken,
    nested?.id_token,
    nested?.result?.accessToken,
    nested?.result?.token,
    result?.accessToken,
    result?.access_token,
    result?.token,
    result?.jwt,
    payload?.accessToken,
    payload?.token,
    payload?.access_token,
  ];

  for (const c of candidates) {
    if (typeof c === 'string' && looksLikeJwt(c)) return c;
  }

  const refreshFallback = [data.refreshToken, data.refresh_token, nested?.refreshToken, nested?.refresh_token];
  for (const c of refreshFallback) {
    if (typeof c === 'string' && looksLikeJwt(c)) return c;
  }

  return deepFindJwtLike(data);
}

/**
 * GET refresh; stores returned token in a cookie.
 * @param {string} currentToken — JWT from URL or last refresh
 * @returns {Promise<string>} new token
 */
export async function refreshAccessToken(currentToken) {
  if (!currentToken) throw new Error('Missing token for refresh');

  const refreshUrl = resolveRefreshUrl();
  const res = await fetch(refreshUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${currentToken}`,
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Token refresh failed (${res.status}): ${text || res.statusText}`);
  }

  const raw = await res.text();
  let data;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    throw new Error(`Token refresh: response was not JSON: ${raw?.slice(0, 200)}`);
  }

  const next = pickTokenFromResponse(data);
  if (!next || typeof next !== 'string') {
    if (process.env.NODE_ENV === 'development') {
      try {
        const keys = data && typeof data === 'object' ? Object.keys(data) : [];
        console.warn('[auth] refreshToken response had no extractable JWT. Top-level keys:', keys);
      } catch {
        /* ignore */
      }
    }
    throw new Error('Token refresh: response did not include a usable token');
  }

  try {
    persistAccessToken(next);
  } catch (e) {
    console.warn('[auth] Could not persist token to cookie', e);
  }
  return next;
}

/** Start / restart the 30-minute refresh timer using whatever is stored in the cookie. */
export function startTokenRefreshInterval() {
  clearRefreshInterval();
  refreshIntervalId = window.setInterval(async () => {
    const t = getAccessToken();
    if (!t) return;
    try {
      await refreshAccessToken(t);
    } catch (e) {
      console.error('[auth] Scheduled token refresh failed', e);
    }
  }, REFRESH_MS);
}

function stripTokenQueryFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    const next =
      `${url.pathname}${url.search}${url.hash}` || `${url.pathname}${url.hash}`;
    window.history.replaceState({}, document.title, next);
  } catch (e) {
    console.warn('[auth] Could not clean URL', e);
  }
}

/**
 * Call once at app bootstrap:
 * - If `?token=` exists: remove it from the URL, exchange token, then schedule 30m refresh.
 * - Else if a token is already stored: schedule 30m refresh only.
 */
export async function initAuthFromUrl() {
  let urlToken = null;
  try {
    urlToken = new URL(window.location.href).searchParams.get('token');
  } catch {
    urlToken = null;
  }

  if (urlToken) {
    stripTokenQueryFromUrl();
    try {
      await refreshAccessToken(urlToken);
      startTokenRefreshInterval();
    } catch (e) {
      console.error('[auth] Initial refresh after URL token failed', e);
      // Refresh may return 200 with an unexpected shape, or fail network/CORS — still try the URL JWT for APIs that accept it
      try {
        persistAccessToken(urlToken);
        startTokenRefreshInterval();
        console.warn(
          '[auth] Using the token from the URL for API calls. If problems persist, check refresh API response shape or REACT_APP_AUTH_REFRESH_URL.',
        );
      } catch (persistErr) {
        console.error('[auth] Could not persist fallback token', persistErr);
      }
    }
    return;
  }

  const existing = getAccessToken();
  if (existing) {
    startTokenRefreshInterval();
  }
}
